import { ReactNode } from "react";
import {
  useQuery,
  useQueries,
  UseQueryOptions,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import {
  QueryClient,
  QueryClientProvider,
  QueryFunctionContext,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import Axios from "axios";
import { API_HOST } from "../env";
import { transformKey, transformKeys, caseMethods } from "../api/case";
import parseQuery from "../util/parseQuery";

const axios = Axios.create({
  baseURL: API_HOST + "/api/v2",
  withCredentials: true,
});

let asUserId = parseQuery().asUserId || undefined;

axios.interceptors.request.use(function (config) {
  if (asUserId) {
    if (config.url!.includes("?")) config.url += "&";
    else config.url += "?";
    config.url += `__as=${asUserId}`;
  }

  config.headers = {
    ...config.headers,
    "Skipio-API-Token":
      window.localStorage.token ||
      window.sessionStorage.token ||
      process.env.REACT_APP_API_TOKEN ||
      "",
    "X-Skipio-Client": "split v1",
  };
  return config;
});

axios.interceptors.response.use(function (response) {
  return {
    ...response,
    data: transformKeys(response.data, caseMethods.camel),
  };
});

const defaultQueryFunction = async ({
  queryKey,
  pageParam,
}: QueryFunctionContext<readonly unknown[]>) => {
  let queryString = "";
  const lastKeySegment = queryKey[queryKey.length - 1];

  if (typeof lastKeySegment === "object" && lastKeySegment !== null) {
    const queryParams = { ...lastKeySegment } as Record<string, unknown>;

    if (pageParam) {
      queryParams.beforeTimestamp = pageParam;
    }

    queryString =
      "?" +
      new URLSearchParams(
        transformKeys(queryParams, caseMethods.snake)
      ).toString();
  }

  const path = queryKey
    .filter((k) => typeof k === "string" || typeof k === "number")
    .map((k) => (typeof k === "number" ? k.toString() : k))
    .map((k) => transformKey(k as string, caseMethods.snake))
    .join("/");

  const { data } = await axios({ method: "GET", url: `${path}${queryString}` });
  return data;
};

// Flatten nested data and meta so that query.data will always have the same shape
// and meta will be accessible via query.meta
function flattenResponseData(resData: any) {
  let data: any = resData;
  let meta: any = null;

  if (resData && typeof resData === "object") {
    if ("data" in resData) {
      data = resData.data;
    }

    if ("meta" in resData) {
      meta = resData.meta;
    }
  }

  return { data, meta };
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFunction,
      suspense: true,
      // TODO: consider re-enabling window focus refetch once pagination optimizations are in place
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (Axios.isAxiosError(error) && error.response?.status !== 401) {
          return failureCount < 3;
        } else {
          return false;
        }
      },
    },
  },
});

export function Provider({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={true} />
    </QueryClientProvider>
  );
}

// Restrict params to a single options obj in preparation for v5
export function useSkipioQuery(queryOptions: UseQueryOptions) {
  const skipioQuery = useQuery({
    ...queryOptions,
  });

  const { data, meta } = flattenResponseData(skipioQuery.data);

  return { ...skipioQuery, data, meta };
}

export function useInfiniteSkipioQuery(queryOptions: UseInfiniteQueryOptions) {
  const skipioQuery = useInfiniteQuery({
    ...queryOptions,
  });

  return {
    ...skipioQuery,
    flattenedData: skipioQuery.data?.pages
      .map((page) => {
        if (typeof page === "object" && page !== null && "data" in page) {
          return (page as any).data;
        } else {
          return page;
        }
      })
      .flat(),
  };
}

export function useSkipioQueries({
  queries,
}: {
  queries: readonly UseQueryOptions[];
}) {
  const skipioQueries = useQueries({
    queries: queries.map((query) => {
      return { ...query, queryFn: defaultQueryFunction };
    }),
  });

  return skipioQueries.map((query) => {
    const { data, meta } = flattenResponseData(query.data);

    return { ...query, data, meta };
  });
}

// export function useStargateQuery()
